import React, { useState, useEffect } from 'react'
import {
  Table, Button, Space, Card, Form, Input, Tabs,
  Select, message, Checkbox, Typography, Drawer, Tooltip, Popconfirm,
  Switch, Tag, Empty, Divider, Avatar, Tooltip as AntTooltip, Col,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SafetyCertificateOutlined,
  EyeOutlined, TeamOutlined,
  CheckCircleOutlined, SearchOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import {
  useMasterData, type Role, type Employee, type ModulePermissions,
} from '@/context/MasterDataContext'
import { generateCode } from '@/utils/codeGenerator'
import { ApiError } from '@/services/api'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import FilterBarCard from '@/components/FilterBarCard'

const { Text } = Typography

const BASE = '/master/roles'

const STATUS_PILL: Record<string, string> = {
  active: 'pill-green', inactive: 'pill-neutral',
}
const STATUS_LABEL: Record<string, string> = {
  active: 'Active', inactive: 'Inactive',
}

const MODULE_LABELS: Record<string, string> = {
  Mapping: 'Generator-Consumer Mapping',
}

const ALL_MODULES = [
  'Masters', 'Generator', 'Consumer', 'Mapping', 'Leads', 'Proposals', 'Agreements',
  'Allocations', 'DCR', 'Accounts', 'Finance', 'Reports',
]

const DEFAULT_PERMISSIONS = ALL_MODULES.reduce((acc, mod) => {
  acc[mod] = { view: false, add: false, edit: false, delete: false, email: false, download: false, approve: false, reject: false }
  return acc
}, {} as Record<string, ModulePermissions>)

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12 }}>
    <div style={{ width: 120, flexShrink: 0, fontSize: 12, color: '#8A93A6', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 13, color: '#0B1426', flex: 1 }}>{value || <span style={{ color: '#ccc' }}>—</span>}</div>
  </div>
)

// ── Main Component ──────────────────────────────────────────────────────────
const RoleMaster: React.FC = () => {
  const navigate = useNavigate()
  const { roles, employees, deleteRole, saveRole, masterDataLoaded } = useMasterData()
  const { mode, editId, isForm, goAdd, goEdit, goList } = useMasterFormRoute(BASE)
  const [viewRecord, setViewRecord] = useState<Role | null>(null)
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState('permissions')
  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('')

  const filteredRoles = roles.filter(r => {
    const q = searchText.toLowerCase()
    const matchSearch = !q ||
      r.name.toLowerCase().includes(q) ||
      (r.description || '').toLowerCase().includes(q)
    const matchStatus = !filterStatus || r.status === filterStatus
    return matchSearch && matchStatus
  })

  // Populate the form when entering the full-page add/edit route
  useEffect(() => {
    if (mode === 'new') {
      form.resetFields()
      form.setFieldsValue({ permissions: DEFAULT_PERMISSIONS, status: 'active' })
      setActiveTab('permissions')
    } else if (mode === 'edit') {
      const rec = roles.find(r => r.id === editId)
      if (rec) {
        form.setFieldsValue(rec)
        setActiveTab('permissions')
      } else {
        goList()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editId, roles])

  const handleDelete = async (roleId: string) => {
    const role = roles.find(r => r.id === roleId)
    if (!role) return
    const isUsed = employees.some(e => e.role === role.name)
    if (isUsed) {
      message.error(`Cannot delete role '${role.name}' — it is assigned to one or more employees.`)
      return
    }
    try {
      await deleteRole(roleId)
      message.success('Role removed successfully')
      if (isForm) goList()
    } catch { /* error already shown */ }
  }

  const onFinish = async (values: any) => {
    const isEdit = mode === 'edit'
    let id = editId!
    if (!isEdit) {
      try {
        id = await generateCode('roles')
      } catch (err) {
        message.error(err instanceof ApiError ? err.message : "Couldn't generate a role code — check your connection and try again.")
        return
      }
    }
    const payload: Role = {
      ...values,
      id,
    }
    setSubmitting(true)
    try {
      await saveRole(payload, isEdit)
      message.success(isEdit ? 'Role updated successfully' : 'Role added successfully')
      goList()
    } catch { /* error already shown; stay on the form */ }
    finally { setSubmitting(false) }
  }

  const columns = [
    {
      title: 'Role ID', dataIndex: 'id', width: 120,
      render: (v: string) => (
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#001A4D', fontSize: 12 }}>{v}</span>
      ),
    },
    {
      title: 'Role Name', dataIndex: 'name',
      render: (name: string, r: Role) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1677ff' }}>
            {name}
          </div>
          <div style={{ fontSize: 11, color: '#8A93A6' }}>{r.description}</div>
        </div>
      ),
    },
    {
      title: 'Employees', width: 220,
      render: (_: any, r: Role) => {
        const assigned = employees.filter(e => e.role === r.name)
        if (!assigned.length) return <span style={{ color: '#CBD5E0', fontSize: 12 }}>No employees</span>
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Avatar.Group maxCount={4} size="small" maxStyle={{ background: '#001A4D', color: '#fff', fontSize: 10 }}>
              {assigned.map(e => (
                <AntTooltip key={e.id} title={`${e.name} · ${e.department || e.email}`}>
                  <Avatar size="small" style={{ background: '#2563EB', fontSize: 10 }}>
                    {e.name.charAt(0).toUpperCase()}
                  </Avatar>
                </AntTooltip>
              ))}
            </Avatar.Group>
            <span style={{ fontSize: 11, color: '#8A93A6' }}>
              {assigned.length} employee{assigned.length !== 1 ? 's' : ''}
            </span>
          </div>
        )
      },
    },
    {
      title: 'Status', dataIndex: 'status', width: 120,
      render: (s: string) => (
        <span className={`pill ${STATUS_PILL[s] || 'pill-neutral'}`}>{STATUS_LABEL[s] || s}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: Role) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Tooltip title="View">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setViewRecord(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
          </Tooltip>
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="link" danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // ── Full-page Add / Edit form ─────────────────────────────────
  if (isForm) {
    return (
      <MasterFormShell
        title={mode === 'edit' ? 'Edit Role' : 'Add Role'}
        subtitle="Organization role — module permissions and approval workflow chains"
        onBack={goList}
        extra={mode === 'edit' && editId ? (
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ marginTop: 16 }}>
          {/* Basic Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="name" label="Role Name" rules={[{ required: true, message: 'Role name is required' }]}>
              <Input placeholder="e.g. Sales Manager" />
            </Form.Item>
            <Form.Item name="status" label="Status" initialValue="active">
              <Select
                options={[
                  { value: 'active',   label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                ]}
              />
            </Form.Item>
          </div>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Brief description of this role" />
          </Form.Item>

          {/* Tabs: Permissions + Workflow */}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            style={{ marginTop: 8 }}
            items={[
              {
                key: 'permissions',
                label: (
                  <span style={{ fontWeight: 600 }}>
                    <SafetyCertificateOutlined /> Module Permissions
                  </span>
                ),
                children: (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Configure granular View, Add, Edit, Delete, Email, Download, Approve, and Reject permissions for each module.
                      </Text>
                      <Space>
                        <Button
                          size="small"
                          type="dashed"
                          onClick={() => {
                            const next: Record<string, ModulePermissions> = {}
                            ALL_MODULES.forEach(m => {
                              next[m] = { view: true, add: true, edit: true, delete: true, email: true, download: true, approve: true, reject: true }
                            })
                            form.setFieldsValue({ permissions: next })
                          }}
                        >
                          Select All
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            const next: Record<string, ModulePermissions> = {}
                            ALL_MODULES.forEach(m => {
                              next[m] = { view: false, add: false, edit: false, delete: false, email: false, download: false, approve: false, reject: false }
                            })
                            form.setFieldsValue({ permissions: next })
                          }}
                        >
                          Clear All
                        </Button>
                      </Space>
                    </div>

                    <div style={{ maxHeight: '420px', overflowY: 'auto', paddingRight: '8px' }}>
                      <Table
                        dataSource={ALL_MODULES.map(m => ({ moduleName: m }))}
                        rowKey="moduleName"
                        pagination={false}
                        size="small"
                        bordered
                        columns={[
                          {
                            title: 'Module', dataIndex: 'moduleName', width: '20%',
                            render: (m) => <Text strong>{MODULE_LABELS[m] || m}</Text>
                          },
                          {
                            title: 'View', align: 'center', width: '10%',
                            render: (_, r) => (
                              <Form.Item name={['permissions', r.moduleName, 'view']} valuePropName="checked" noStyle>
                                <Checkbox />
                              </Form.Item>
                            )
                          },
                          {
                            title: 'Add', align: 'center', width: '10%',
                            render: (_, r) => (
                              <Form.Item name={['permissions', r.moduleName, 'add']} valuePropName="checked" noStyle>
                                <Checkbox />
                              </Form.Item>
                            )
                          },
                          {
                            title: 'Edit', align: 'center', width: '10%',
                            render: (_, r) => (
                              <Form.Item name={['permissions', r.moduleName, 'edit']} valuePropName="checked" noStyle>
                                <Checkbox />
                              </Form.Item>
                            )
                          },
                          {
                            title: 'Delete', align: 'center', width: '10%',
                            render: (_, r) => (
                              <Form.Item name={['permissions', r.moduleName, 'delete']} valuePropName="checked" noStyle>
                                <Checkbox />
                              </Form.Item>
                            )
                          },
                          {
                            title: 'Email', align: 'center', width: '10%',
                            render: (_, r) => (
                              <Form.Item name={['permissions', r.moduleName, 'email']} valuePropName="checked" noStyle>
                                <Checkbox />
                              </Form.Item>
                            )
                          },
                          {
                            title: 'Download', align: 'center', width: '10%',
                            render: (_, r) => (
                              <Form.Item name={['permissions', r.moduleName, 'download']} valuePropName="checked" noStyle>
                                <Checkbox />
                              </Form.Item>
                            )
                          },
                          {
                            title: 'Approve', align: 'center', width: '10%',
                            render: (_, r) => (
                              <Form.Item name={['permissions', r.moduleName, 'approve']} valuePropName="checked" noStyle>
                                <Checkbox />
                              </Form.Item>
                            )
                          },
                          {
                            title: 'Reject', align: 'center', width: '10%',
                            render: (_, r) => (
                              <Form.Item name={['permissions', r.moduleName, 'reject']} valuePropName="checked" noStyle>
                                <Checkbox />
                              </Form.Item>
                            )
                          },
                        ]}
                      />
                    </div>
                  </div>
                ),
              },
            ]}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>{mode === 'edit' ? 'Update Role' : 'Add Role'}</Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  // ── List ───────────────────────────────────────────────────────
  if (!masterDataLoaded) return <TableSkeleton columns={5} />

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SafetyCertificateOutlined style={{ color: '#2563EB' }} /> Role Master
          </h2>
          <p className="page-subtitle">Manage organization roles, module permissions, and approval workflow chains</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={goAdd}>Add Role</Button>
      </div>

      <KpiCardRow
        cards={[
          { label: 'Total Roles', value: roles.length, icon: <SafetyCertificateOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active', value: roles.filter(r => r.status === 'active').length, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
        ]}
      />

      <FilterBarCard>
        <Col xs={24} sm={12} lg={10}>
          <Input.Search
            placeholder="Search by role name..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(v) => setSearchText(v)}
          />
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(v) => setFilterStatus(v || '')}
            style={{ width: '100%' }}
            options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]}
          />
        </Col>
      </FilterBarCard>

      <Card>
        <Table
          dataSource={filteredRoles}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} roles` }}
        />
      </Card>

      {/* ── Detail Drawer ── */}
      <Drawer
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{viewRecord?.name}</div>
            <div style={{ fontSize: 12, color: '#8A93A6' }}>{viewRecord?.id}</div>
          </div>
        }
        open={!!viewRecord}
        onClose={() => setViewRecord(null)}
        width={500}
        extra={
          <Space>
            <Tooltip title="Edit">
              <Button type="text" icon={<EditOutlined />} onClick={() => { if (viewRecord) goEdit(viewRecord.id); setViewRecord(null) }} />
            </Tooltip>
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete this record?"
                description="This action cannot be undone."
                onConfirm={() => { handleDelete(viewRecord!.id); setViewRecord(null) }}
                okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          </Space>
        }
      >
        {viewRecord && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <span className={`pill ${STATUS_PILL[viewRecord.status] || 'pill-neutral'}`}>
                {STATUS_LABEL[viewRecord.status] || viewRecord.status}
              </span>
            </div>
            <DetailRow label="Role Name" value={<strong>{viewRecord.name}</strong>} />
            <DetailRow label="Description" value={viewRecord.description} />

            {/* Employees with this Role */}
            {(() => {
              const assigned = employees.filter(e => e.role === viewRecord.name)
              return (
                <div style={{ marginTop: 16, marginBottom: 4 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#8A93A6', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <TeamOutlined /> Employees with this Role ({assigned.length})
                  </div>
                  {assigned.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#CBD5E0', fontStyle: 'italic' }}>No employees assigned yet</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {assigned.map(e => (
                        <div
                          key={e.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '7px 10px', borderRadius: 8,
                            background: '#F8FAFF', border: '1px solid #E4E8F0',
                            cursor: 'pointer', transition: 'background 0.15s',
                          }}
                          onClick={() => navigate('/master/users')}
                        >
                          <Avatar size={28} style={{ background: '#2563EB', fontSize: 11, flexShrink: 0 }}>
                            {e.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: '#0B1426', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.name}</div>
                            <div style={{ fontSize: 11, color: '#8A93A6' }}>{e.department || ''}{e.department && e.email ? ' · ' : ''}{e.email}</div>
                          </div>
                          <Tag color="blue" style={{ fontSize: 10, flexShrink: 0 }}>{e.region}</Tag>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Permissions */}
            {viewRecord.permissions && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#8A93A6', marginBottom: 12 }}>
                  Module Permissions
                </div>
                {Object.entries(viewRecord.permissions).map(([mod, perms]: [string, any]) => {
                  const granted = Object.entries(perms).filter(([, v]) => v).map(([k]) => k)
                  if (!granted.length) return null
                  return (
                    <div key={mod} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12 }}>
                      <div style={{ width: 120, flexShrink: 0, fontSize: 12, color: '#8A93A6', fontWeight: 500 }}>{mod}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {granted.map(g => (
                          <span key={g} style={{ fontSize: 11, padding: '2px 8px', background: '#EFF6FF', color: '#2563EB', borderRadius: 4, fontWeight: 600, textTransform: 'capitalize' }}>
                            {g}
                          </span>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

          </div>
        )}
      </Drawer>
    </div>
  )
}

export default RoleMaster
